import { useNavigate } from "react-router";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { useAppState, useExitViewAs } from "@/lib/queries";
import { useToast } from "@/components/ui/Toast";

interface AppShape {
  uiAccess?: {
    viewAs?: {
      active?: boolean;
      impersonatedAccountId?: string | null;
      ownerUserId?: string;
    };
  };
  tenancy?: {
    accounts?: Array<{ id: string; account?: { companyName?: string } }>;
  };
}

/**
 * Persistent banner shown only when a Dorsey-Worx platform member is
 * acting inside a customer tenant. Names the tenant and reminds the user
 * that every action is recorded on their own audit trail. The "Exit"
 * button clears the account cookie via /api/accounts/exit-view-as, which
 * also emits the matching view_as.exited audit event server-side.
 */
export function ViewAsBanner() {
  const { data: appRaw } = useAppState();
  const exitMut = useExitViewAs();
  const { toast } = useToast();
  const navigate = useNavigate();

  const app = (appRaw ?? {}) as AppShape;
  const viewAs = app.uiAccess?.viewAs;
  if (!viewAs?.active || !viewAs.impersonatedAccountId) return null;

  const tenantName =
    app.tenancy?.accounts?.find(
      (entry) => entry.id === viewAs.impersonatedAccountId,
    )?.account?.companyName || viewAs.impersonatedAccountId;

  const handleExit = () => {
    exitMut.mutate(undefined, {
      onSuccess: () => {
        navigate("/system/overview");
      },
      onError: (err) =>
        toast({
          title: "Couldn't exit view-as",
          description: (err as Error).message,
          tone: "error",
        }),
    });
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className="sticky top-0 z-40 w-full bg-tone-ember-soft border-b border-tone-ember/40 px-dw-4 py-dw-2 flex flex-wrap items-center gap-dw-3"
    >
      <Badge tone="ember">VIEW AS</Badge>
      <span className="text-sm text-dw-text font-medium">
        Viewing as <span className="font-semibold">{tenantName}</span> — every
        action is logged to your Dorsey-Worx audit trail.
      </span>
      <div className="ml-auto">
        <Button
          size="sm"
          variant="outline"
          onClick={handleExit}
          loading={exitMut.isPending}
        >
          Exit to System
        </Button>
      </div>
    </div>
  );
}
